import React, { useState } from 'react';

interface OrderFormProps {
  onSubmit: (formData: OrderFormData) => void;
  onCancel: () => void;
  cartItems: Array<{
    id: string;
    name: string;
    price: number;
    quantity: number;
    category: string;
    is_pack?: boolean;
    pack_items?: Array<{
      id: string;
      quantity: number;
      category: string;
    }>;
  }>;
}

export interface OrderFormData {
  firstName: string;
  lastName: string;
  isFaidherbard: boolean;
  class?: string;
  phoneNumber: string;
  paymentMethod: 'paypal' | 'cash' | 'rib';
  tshirtSize?: 'XS' | 'S' | 'M' | 'L' | 'XL';
}

export const OrderForm = ({ onSubmit, onCancel, cartItems = [] }: OrderFormProps) => {
  const [formData, setFormData] = useState<OrderFormData>({
    firstName: '',
    lastName: '',
    isFaidherbard: false,
    phoneNumber: '',
    paymentMethod: 'paypal'
  });

  const [hasTshirt, setHasTshirt] = useState(false);

  // Vérifier si le panier contient des T-shirts ou des packs contenant des T-shirts
  const hasTshirtInCart = cartItems.some(item => {
    if (item.category === 'merch') {
      return true;
    }
    if (item.is_pack && item.pack_items) {
      return item.pack_items.some(packItem => packItem.category === 'merch');
    }
    return false;
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
            Prénom
          </label>
          <input
            type="text"
            id="firstName"
            required
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
          />
        </div>

        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
            Nom
          </label>
          <input
            type="text"
            id="lastName"
            required
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
          />
        </div>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="isFaidherbard"
          checked={formData.isFaidherbard}
          onChange={(e) => setFormData({ ...formData, isFaidherbard: e.target.checked })}
          className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
        />
        <label htmlFor="isFaidherbard" className="ml-2 block text-sm text-gray-700">
          Je suis un/e Faidherbard/e
        </label>
      </div>

      {formData.isFaidherbard && (
        <div>
          <label htmlFor="class" className="block text-sm font-medium text-gray-700">
            Classe
          </label>
          <select
            id="class"
            required={formData.isFaidherbard}
            value={formData.class || ''}
            onChange={(e) => setFormData({ ...formData, class: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
          >
            <option value="">Sélectionnez votre classe</option>
            <option value="HX1">HX1</option>
            <option value="HX2">HX2</option>
            <option value="HX3">HX3</option>
            <option value="HX4">HX4</option>
            <option value="HX5">HX5</option>
            <option value="HK1">HK1</option>
            <option value="HK2">HK2</option>
            <option value="HK3">HK3</option>
            <option value="HKBL">HKBL</option>
            <option value="BCPSTa">BCPSTa</option>
            <option value="BCPSTb">BCPSTb</option>
            <option value="HEC">HEC</option>
          </select>
        </div>
      )}

      <div>
        <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
          Numéro de téléphone
        </label>
        <input
          type="tel"
          id="phoneNumber"
          required
          value={formData.phoneNumber}
          onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
          placeholder="06 12 34 56 78"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Mode de paiement
        </label>
        <div className="space-y-2">
          <div className="flex items-center">
            <input
              type="radio"
              id="paypal"
              name="paymentMethod"
              value="paypal"
              checked={formData.paymentMethod === 'paypal'}
              onChange={(e) => setFormData({ ...formData, paymentMethod: 'paypal' })}
              className="h-4 w-4 text-pink-600 focus:ring-pink-500"
            />
            <label htmlFor="paypal" className="ml-2 block text-sm text-gray-700">
              PayPal
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="radio"
              id="cash"
              name="paymentMethod"
              value="cash"
              checked={formData.paymentMethod === 'cash'}
              onChange={(e) => setFormData({ ...formData, paymentMethod: 'cash' })}
              className="h-4 w-4 text-pink-600 focus:ring-pink-500"
            />
            <label htmlFor="cash" className="ml-2 block text-sm text-gray-700">
              Espèces
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="radio"
              id="rib"
              name="paymentMethod"
              value="rib"
              checked={formData.paymentMethod === 'rib'}
              onChange={(e) => setFormData({ ...formData, paymentMethod: 'rib' })}
              className="h-4 w-4 text-pink-600 focus:ring-pink-500"
            />
            <label htmlFor="rib" className="ml-2 block text-sm text-gray-700">
              RIB
            </label>
          </div>
        </div>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="hasTshirt"
          checked={hasTshirt}
          onChange={(e) => setHasTshirt(e.target.checked)}
          className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
        />
        <label htmlFor="hasTshirt" className="ml-2 block text-sm text-gray-700">
          J'ai commandé un T-shirt
        </label>
      </div>

      {hasTshirt && (
        <div>
          <label htmlFor="tshirtSize" className="block text-sm font-medium text-gray-700">
            Taille du T-shirt
          </label>
          <select
            id="tshirtSize"
            required={hasTshirt}
            value={formData.tshirtSize || ''}
            onChange={(e) => setFormData({ ...formData, tshirtSize: e.target.value as OrderFormData['tshirtSize'] })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
          >
            <option value="">Sélectionnez votre taille</option>
            <option value="XS">XS</option>
            <option value="S">S</option>
            <option value="M">M</option>
            <option value="L">L</option>
            <option value="XL">XL</option>
          </select>
        </div>
      )}

      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Annuler
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600"
        >
          Confirmer la commande
        </button>
      </div>
    </form>
  );
}; 